import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Divider
} from '@mui/material';
import {
  Business,
  LocationOn,
  LocalShipping,
  AccessTime,
  Star,
  CheckCircle,
  ArrowBack,
  ArrowForward,
  Search,
  History,
  CheckCircleOutline
} from '@mui/icons-material';
import api from '../../services/api';
import { brand, bg } from '../../theme/colors';

const MerchantSelector = ({ project, requestData, onNext, onBack, onCancel, preSelectedMerchantId }) => {
  const [merchants, setMerchants] = useState([]);
  const [filteredMerchants, setFilteredMerchants] = useState([]);
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  const [previouslyUsedMerchants, setPreviouslyUsedMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    location: '',
    rating: '',
    specialization: '',
    tier: ''
  });

  useEffect(() => {
    fetchMerchants();
    fetchPreviouslyUsedMerchants();
  }, []);

  // Pre-select merchant if provided
  useEffect(() => {
    if (preSelectedMerchantId && merchants.length > 0) {
      const merchant = merchants.find(m => (m.user_id || m.id) === preSelectedMerchantId);
      if (merchant && !selectedMerchants.includes(preSelectedMerchantId)) {
        setSelectedMerchants([preSelectedMerchantId]);
      }
    }
  }, [preSelectedMerchantId, merchants, selectedMerchants]);

  useEffect(() => {
    applyFilters();
  }, [merchants, searchTerm, filters, requestData?.materialCategories]);

  const fetchMerchants = async () => {
    try {
      setLoading(true);
      setError('');

      // Build query params - don't filter by default, let user use filters
      const params = {
        verificationStatus: 'Verified',
        accountStatus: 'Active',
        limit: 100
      };

      // Only filter by material categories if provided (optional)
      // Don't auto-filter by location - let user choose

      const response = await api.get('/merchants', { params });

      if (response.data.success) {
        setMerchants(response.data.data || []);
        setFilteredMerchants(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching merchants:', err);
      setError('Failed to load merchants. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviouslyUsedMerchants = async () => {
    try {
      const usedMerchantIds = new Set();
      const usedMerchantsMap = new Map();

      // Get merchants from previous orders
      try {
        const ordersResponse = await api.get('/material-orders?role=client');

        if (ordersResponse.data.success && ordersResponse.data.data) {
          ordersResponse.data.data.forEach(order => {
            // merchant_profile_id is the field in material_orders
            const merchantId = order.merchant_profile_id || order.merchant_id;
            if (merchantId && !usedMerchantIds.has(merchantId)) {
              usedMerchantIds.add(merchantId);
              usedMerchantsMap.set(merchantId, {
                id: merchantId,
                name: order.merchant_name,
                lastUsed: order.created_at || order.updated_at,
                orderCount: 1,
                type: 'order'
              });
            }
          });
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
      }

      // Get merchants from previous quote requests
      try {
        const quotesResponse = await api.get('/merchant-quotes/requests');

        if (quotesResponse.data.success && quotesResponse.data.data) {
          quotesResponse.data.data.forEach(request => {
            // selected_merchant_ids is an array in quote requests
            if (request.selected_merchant_ids && Array.isArray(request.selected_merchant_ids)) {
              request.selected_merchant_ids.forEach(merchantId => {
                if (merchantId && !usedMerchantIds.has(merchantId)) {
                  usedMerchantIds.add(merchantId);
                  usedMerchantsMap.set(merchantId, {
                    id: merchantId,
                    name: 'Merchant',
                    lastUsed: request.created_at,
                    orderCount: 0,
                    type: 'quote'
                  });
                }
              });
            }
          });
        }
      } catch (err) {
        console.error('Error fetching quote requests:', err);
      }

      // Fetch full merchant profiles for used merchants
      if (usedMerchantIds.size > 0) {
        const merchantIds = Array.from(usedMerchantIds);
        const merchantsResponse = await api.get('/merchants', { params: { limit: 100 } });

        if (merchantsResponse.data.success && merchantsResponse.data.data) {
          const usedMerchants = merchantsResponse.data.data.filter(m => {
            const merchantId = m.user_id || m.id;
            return merchantIds.some(id => {
              // Match by user_id or id
              return id === merchantId || id === m.id;
            });
          }).map(merchant => ({
            ...merchant,
            isPreviouslyUsed: true,
            lastUsed: usedMerchantsMap.get(merchant.user_id || merchant.id)?.lastUsed
          }));

          setPreviouslyUsedMerchants(usedMerchants);
        }
      }
    } catch (err) {
      console.error('Error fetching previously used merchants:', err);
      // Don't fail the whole component if history fetch fails
    }
  };

  const applyFilters = () => {
    let filtered = [...merchants];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(merchant =>
        merchant.business_name?.toLowerCase().includes(searchLower) ||
        merchant.trading_name?.toLowerCase().includes(searchLower) ||
        merchant.specializations?.some(s => s.toLowerCase().includes(searchLower))
      );
    }

    // Location filter
    if (filters.location) {
      filtered = filtered.filter(merchant =>
        merchant.service_areas?.includes(filters.location) ||
        merchant.physical_address?.city === filters.location
      );
    }

    // Rating filter
    if (filters.rating) {
      const minRating = parseFloat(filters.rating);
      filtered = filtered.filter(merchant => {
        const rating = merchant.rating_average ? parseFloat(merchant.rating_average) : 0;
        return rating >= minRating;
      });
    }

    // Specialization filter
    if (filters.specialization) {
      filtered = filtered.filter(merchant =>
        merchant.specializations?.includes(filters.specialization)
      );
    }

    // Tier filter
    if (filters.tier) {
      filtered = filtered.filter(merchant =>
        merchant.verification_tier === filters.tier
      );
    }

    // Material category match (recommend merchants matching requested categories)
    if (requestData?.materialCategories && requestData.materialCategories.length > 0) {
      filtered = filtered.map(merchant => ({
        ...merchant,
        matchScore: calculateMatchScore(merchant, requestData.materialCategories)
      })).sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));
    }

    setFilteredMerchants(filtered);
  };

  const calculateMatchScore = (merchant, categories) => {
    if (!merchant.specializations || !categories) return 0;
    
    const merchantSpecs = merchant.specializations.map(s => s.toLowerCase());
    const requestedSpecs = categories.map(c => c.toLowerCase());
    
    const matches = requestedSpecs.filter(spec =>
      merchantSpecs.some(ms => ms.includes(spec) || spec.includes(ms))
    );
    
    return matches.length / categories.length;
  };

  const toggleMerchantSelection = (merchantId) => {
    setSelectedMerchants(prev =>
      prev.includes(merchantId)
        ? prev.filter(id => id !== merchantId)
        : [...prev, merchantId]
    );
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'Tier3': return 'Premium Merchant';
      case 'Tier2': return 'Verified Merchant';
      case 'Tier1': return 'Basic Merchant';
      default: return 'Basic Merchant';
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Tier3': return 'success';
      case 'Tier2': return 'primary';
      case 'Tier1': return 'default';
      default: return 'default';
    }
  };

  const handleNext = () => {
    if (selectedMerchants.length === 0) {
      setError('Please select at least one merchant to send quote requests to');
      return;
    }

    if (!onNext) {
      console.error('onNext callback is not defined');
      setError('Unable to proceed. Please try again.');
      return;
    }

    try {
      const dataToPass = {
        ...requestData,
        selectedMerchantIds: selectedMerchants
      };
      
      console.log('Sending merchant selection data:', dataToPass);
      onNext(dataToPass);
    } catch (error) {
      console.error('Error in handleNext:', error);
      setError('An error occurred. Please try again.');
    }
  };

  const recommendedMerchants = filteredMerchants
    .filter(m => {
      // Don't show previously used merchants in recommended
      const isPreviouslyUsed = previouslyUsedMerchants.some(pm => 
        (pm.user_id || pm.id) === (m.user_id || m.id)
      );
      return m.matchScore > 0 && !isPreviouslyUsed;
    })
    .slice(0, 3);

  // Filter out previously used merchants from all merchants list
  const allMerchants = filteredMerchants.filter(m => {
    const isPreviouslyUsed = previouslyUsedMerchants.some(pm => 
      (pm.user_id || pm.id) === (m.user_id || m.id)
    );
    return !isPreviouslyUsed;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Select Suppliers to Send Quote Request
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onBack && (
            <Button size="small" onClick={onBack} startIcon={<ArrowBack />}>
              Back
            </Button>
          )}
          {onCancel && (
            <Button size="small" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Box>
      </Box>

      {/* Request Summary */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: bg.page }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Your Request Summary:
        </Typography>
        <Typography variant="body2">
          • Materials: {requestData?.materialCategories?.join(', ') || 'Various'}
        </Typography>
        <Typography variant="body2">
          • Delivery: {project?.location || 'TBD'} ({requestData?.deliveryTimeframe || 'TBD'})
        </Typography>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose which suppliers should receive your quote request. You can select multiple suppliers to get competitive quotes.
      </Typography>

      {/* Search and Filters */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search suppliers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ mb: 2 }}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Location</InputLabel>
              <Select
                value={filters.location}
                label="Location"
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              >
                <MenuItem value="">All Locations</MenuItem>
                <MenuItem value="Harare">Harare</MenuItem>
                <MenuItem value="Bulawayo">Bulawayo</MenuItem>
                <MenuItem value="Chitungwiza">Chitungwiza</MenuItem>
                <MenuItem value="Mutare">Mutare</MenuItem>
                <MenuItem value="Gweru">Gweru</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Rating</InputLabel>
              <Select
                value={filters.rating}
                label="Rating"
                onChange={(e) => setFilters({ ...filters, rating: e.target.value })}
              >
                <MenuItem value="">All Ratings</MenuItem>
                <MenuItem value="4.5">4.5+ Stars</MenuItem>
                <MenuItem value="4.0">4.0+ Stars</MenuItem>
                <MenuItem value="3.5">3.5+ Stars</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Specialization</InputLabel>
              <Select
                value={filters.specialization}
                label="Specialization"
                onChange={(e) => setFilters({ ...filters, specialization: e.target.value })}
              >
                <MenuItem value="">All Specializations</MenuItem>
                {requestData?.materialCategories?.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tier Level</InputLabel>
              <Select
                value={filters.tier}
                label="Tier Level"
                onChange={(e) => setFilters({ ...filters, tier: e.target.value })}
              >
                <MenuItem value="">All Tiers</MenuItem>
                <MenuItem value="Tier1">Tier 1 - Basic</MenuItem>
                <MenuItem value="Tier2">Tier 2 - Verified</MenuItem>
                <MenuItem value="Tier3">Tier 3 - Premium</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Previously Used Merchants */}
      {previouslyUsedMerchants.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <History sx={{ color: brand.primary }} />
            Previously Used Suppliers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Suppliers you've worked with before - quick select:
          </Typography>
          <Grid container spacing={2}>
            {previouslyUsedMerchants.map((merchant) => (
              <Grid item xs={12} key={merchant.user_id || merchant.id}>
                <Card
                  sx={{
                    border: selectedMerchants.includes(merchant.user_id || merchant.id) ? 2 : 1,
                    borderColor: selectedMerchants.includes(merchant.user_id || merchant.id) ? 'primary.main' : 'grey.300',
                    cursor: 'pointer',
                    transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    bgcolor: selectedMerchants.includes(merchant.user_id || merchant.id) ? 'primary.light' : 'background.paper',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => toggleMerchantSelection(merchant.user_id || merchant.id)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                      <Checkbox
                        checked={selectedMerchants.includes(merchant.user_id || merchant.id)}
                        onChange={() => toggleMerchantSelection(merchant.user_id || merchant.id)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ mt: -1, ml: -1 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Business sx={{ fontSize: 20 }} />
                              {merchant.business_name || 'Unknown Business'}
                              {merchant.verification_status === 'Verified' && (
                                <CheckCircle color="primary" sx={{ fontSize: 20 }} />
                              )}
                              <Chip
                                label="Used Before"
                                size="small"
                                icon={<CheckCircleOutline />}
                                color="success"
                                sx={{ ml: 1 }}
                              />
                            </Typography>
                            <Chip
                              label={getTierLabel(merchant.verification_tier)}
                              color={getTierColor(merchant.verification_tier)}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Star sx={{ fontSize: 18, color: '#ffc107' }} />
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {merchant.rating_average ? parseFloat(merchant.rating_average).toFixed(1) : '0.0'}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              ({merchant.total_ratings || 0} ratings)
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {merchant.physical_address?.city || merchant.service_areas?.[0] || 'Location TBD'}
                            </Typography>
                          </Box>
                          {merchant.delivery_options?.offers_delivery && (
                            <Chip
                              icon={<LocalShipping sx={{ fontSize: 14 }} />}
                              label="Offers Delivery"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              Avg Response: {merchant.quote_response_time_avg || 'N/A'} hours
                            </Typography>
                          </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Specialties: {merchant.specializations?.join(', ') || 'Various'}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          Orders Completed: {merchant.total_orders_completed || 0} | On-Time Rate: {merchant.on_time_delivery_rate || 0}%
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {(previouslyUsedMerchants.length > 0) && <Divider sx={{ my: 3 }} />}

      {/* Recommended Merchants */}
      {recommendedMerchants.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Recommended Suppliers (Based on your materials and location)
          </Typography>
          <Grid container spacing={2}>
            {recommendedMerchants.map((merchant) => (
              <Grid item xs={12} key={merchant.user_id || merchant.id}>
                <MerchantCard
                  merchant={merchant}
                  selected={selectedMerchants.includes(merchant.user_id || merchant.id)}
                  onToggle={() => toggleMerchantSelection(merchant.user_id || merchant.id)}
                  getTierLabel={getTierLabel}
                  getTierColor={getTierColor}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      {/* All Merchants */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          All Suppliers ({allMerchants.length} total)
        </Typography>
        <Grid container spacing={2}>
          {allMerchants.map((merchant) => (
            <Grid item xs={12} key={merchant.user_id || merchant.id}>
              <MerchantCard
                merchant={merchant}
                selected={selectedMerchants.includes(merchant.user_id || merchant.id)}
                onToggle={() => toggleMerchantSelection(merchant.user_id || merchant.id)}
                getTierLabel={getTierLabel}
                getTierColor={getTierColor}
              />
            </Grid>
          ))}
        </Grid>

        {allMerchants.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No merchants found matching your criteria. Try adjusting your filters.
          </Alert>
        )}
      </Box>

      {/* Selection Summary */}
      {selectedMerchants.length > 0 && (
        <Paper elevation={2} sx={{ p: 2, mt: 3, bgcolor: 'primary.light' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            SELECTED: {selectedMerchants.length} supplier{selectedMerchants.length !== 1 ? 's' : ''}
          </Typography>
          {selectedMerchants.map(merchantId => {
            const merchant = merchants.find(m => (m.user_id || m.id) === merchantId);
            return (
              <Typography key={merchantId} variant="body2" sx={{ ml: 2 }}>
                • {merchant?.business_name || 'Unknown'}
              </Typography>
            );
          })}
        </Paper>
      )}

      {/* Tip */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <strong>💡 TIP:</strong> Selecting 3-5 suppliers gives you good price comparison without overwhelming yourself with too many quotes.
      </Alert>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 3 }}>
        <Button onClick={onCancel || onBack} variant="outlined">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Send Quote Requests button clicked, selectedMerchants:', selectedMerchants);
            handleNext();
          }}
          endIcon={<ArrowForward />}
          disabled={selectedMerchants.length === 0}
          sx={{
            background: brand.gradient,
            '&:hover': {
              background: brand.gradientHover
            },
            '&:disabled': {
              background: 'grey.300'
            }
          }}
        >
          Send Quote Requests ({selectedMerchants.length})
        </Button>
      </Box>
    </Box>
  );
};

// Merchant Card Component
const MerchantCard = ({ merchant, selected, onToggle, getTierLabel, getTierColor }) => {
  return (
    <Card
      sx={{
        border: selected ? 2 : 1,
        borderColor: selected ? 'primary.main' : 'grey.300',
        cursor: 'pointer',
        transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)'
        }
      }}
      onClick={onToggle}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
          <Checkbox
            checked={selected}
            onChange={onToggle}
            onClick={(e) => e.stopPropagation()}
            sx={{ mt: -1, ml: -1 }}
          />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business sx={{ fontSize: 20 }} />
                  {merchant.business_name || 'Unknown Business'}
                  {merchant.verification_status === 'Verified' && (
                    <CheckCircle color="primary" sx={{ fontSize: 20 }} />
                  )}
                </Typography>
                <Chip
                  label={getTierLabel(merchant.verification_tier)}
                  color={getTierColor(merchant.verification_tier)}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Star sx={{ fontSize: 18, color: '#ffc107' }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {merchant.rating_average ? parseFloat(merchant.rating_average).toFixed(1) : '0.0'}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  ({merchant.total_ratings || 0} ratings)
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {merchant.physical_address?.city || merchant.service_areas?.[0] || 'Location TBD'}
                </Typography>
              </Box>
              {merchant.delivery_options?.offers_delivery && (
                <Chip
                  icon={<LocalShipping sx={{ fontSize: 14 }} />}
                  label="Offers Delivery"
                  size="small"
                  variant="outlined"
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Avg Response: {merchant.quote_response_time_avg || 'N/A'} hours
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Specialties: {merchant.specializations?.join(', ') || 'Various'}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Orders Completed: {merchant.total_orders_completed || 0} | On-Time Rate: {merchant.on_time_delivery_rate || 0}%
            </Typography>

            {merchant.verification_tier === 'Tier3' && (
              <Chip
                label="🏆 PREMIUM SUPPLIER - Volume discounts available"
                size="small"
                color="success"
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MerchantSelector;

